import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { validateCode, LARGE_SOURCE_BYTES } from '../core/wasm.js';
import { buildPreviewDocument } from './runner/preview-document.js';
import { getSandboxUrl } from '../core/sandbox-origin.js';
import {
  configureTransportHooks,
  getActiveSandboxUrl,
  isActiveSandboxReady,
  postToSandbox,
  queueSandboxPayload,
  showPreviewNotice,
  showPreviewUnavailable
} from './runner/transport.js';
import { markPreviewTiming, startPreviewTiming } from './runner/timing.js';
import { captureAnalyticsEvent } from '../core/analytics.js';
import { createRunPolicy, hashWorkspace, RUN_TRIGGERS } from './runner/run-policy.js';

export { configureExport, exportCode, registerExportDialog } from './runner/export.js';
export { previewBridge } from './runner/preview-bridge.js';
export { buildPreviewDocument } from './runner/preview-document.js';
export { finishPreviewTimingById } from './runner/timing.js';
export { fallbackToSameOriginPreview, getActiveSandboxOrigin, resetTransport, setSandboxReady, showPreviewUnavailable } from './runner/transport.js';
export { RUN_TRIGGERS } from './runner/run-policy.js';

/**
 * Front-my-End — Code Execution Feature
 * Coordinates validation, CSS HMR, preview document rendering, and sandbox transport.
 *
 * Run triggers (see runner/run-policy.js):
 * - `explicit` (Run button, Cmd/Ctrl+Enter, template load/reset, import) renders
 *   the editor JS and records it as the last-run JS.
 * - `implicit` (Live, theme switch, Cmd/Ctrl+S, preview navigation, boot
 *   restore) renders the last-run JS, never unrun editor JS.
 */
const runPolicy = createRunPolicy();
let runnerServices = {
  console: null,
  isMobile: () => false,
  setActiveMobileTab: () => {},
  restartPreviewSandbox: null,
  onJsRunStateChange: null
};

configureTransportHooks({
  onRenderPosted: (timingId) => runPolicy.markRenderPosted(timingId),
  onUnavailable: () => {
    runPolicy.clearRenderInFlight();
    captureAnalyticsEvent('preview_unavailable');
  }
});

export function configureRunner(services = {}) {
  runnerServices = {
    ...runnerServices,
    ...services
  };
}

export function isJsPending() {
  return runPolicy.isJsPending(store.get('js'));
}

/**
 * Called when the sandbox reports a finished render. Clears the safe-boot flag
 * and re-sends the current CSS if it changed while the render was in flight.
 */
export function handlePreviewRenderComplete(timingId) {
  const css = store.get('css') ?? '';
  const resyncCss = runPolicy.completeRender(timingId, css);
  if (resyncCss !== null && getActiveSandboxUrl() && isActiveSandboxReady()) {
    postToSandbox({ type: 'style-update', css: resyncCss });
  }
}

export function handlePreviewScroll(x, y) {
  runPolicy.setScroll(x, y);
}

function ensureSandboxForExplicitRun() {
  if (getActiveSandboxUrl()) return;
  // A fallen-back or unavailable transport stays dead until reset. An explicit
  // Run is the user asking to try again, so restart the configured sandbox.
  if (getSandboxUrl() && typeof runnerServices.restartPreviewSandbox === 'function') {
    runnerServices.restartPreviewSandbox();
  }
}

export async function runCode(options = {}) {
  const trigger = options.trigger === RUN_TRIGGERS.EXPLICIT ? RUN_TRIGGERS.EXPLICIT : RUN_TRIGGERS.IMPLICIT;
  const silent = Boolean(options.silent);
  const html = store.get('html') ?? '';
  const css = store.get('css') ?? '';
  const js = store.get('js') ?? '';
  const theme = store.get('activeTheme');
  const activeRoute = store.get('activePreviewRoute');
  const activePath = store.get('activePreviewPath');

  if (trigger === RUN_TRIGGERS.EXPLICIT) {
    ensureSandboxForExplicitRun();
  }

  if (!silent) {
    [
      ['HTML', 'html', html],
      ['CSS', 'css', css],
      ['JS', 'js', js]
    ].forEach(([label, lang, code]) => {
      // Skip Rust-side validation for a buffer large enough that scanning it
      // on every Run would be noticeable, rather than trying to shrink or
      // window it (unlike highlighting, a partial validation pass isn't
      // meaningful). Mirrors the Format action's size guard.
      if (code.length > LARGE_SOURCE_BYTES) {
        runnerServices.console?.system(`${label} is too large to check for warnings (over ${Math.round(LARGE_SOURCE_BYTES / 1024)} KB), so validation was skipped.`);
        return;
      }
      validateCode(code, lang).forEach((warning) => {
        runnerServices.console?.append('warn', `[Validation] ${label} line ${warning.line}: ${warning.message}`, {
          source: 'Validator',
          line: warning.line
        });
      });
    });
  }

  const plan = runPolicy.planRun({
    trigger,
    html,
    js,
    theme,
    activePath: activePath || 'index.html',
    allowHotSwap: Boolean(options.allowHotSwap)
  });
  if (trigger === RUN_TRIGGERS.EXPLICIT || options.boot) {
    runnerServices.onJsRunStateChange?.();
  }

  if (plan.mode === 'style') {
    if (getActiveSandboxUrl()) {
      // Not queued when the sandbox is not ready: the pending render, or the
      // re-sync after it completes, already carries the current CSS.
      if (isActiveSandboxReady()) {
        postToSandbox({ type: 'style-update', css });
      }
    } else {
      showPreviewUnavailable('Preview rendering requires a cross-origin sandbox. Same-origin srcdoc execution is disabled in production.');
    }
    return;
  }

  const workspaceHash = hashWorkspace(html, plan.effectiveJs);
  if (options.boot && runPolicy.isUnfinishedWorkspace(workspaceHash)) {
    runPolicy.invalidateBaked();
    showPreviewNotice(
      'Preview paused',
      "The last preview of this workspace didn't finish. It may contain an infinite loop. Check your JavaScript, then press Run."
    );
    runnerServices.console?.append('error', "The last preview of this workspace didn't finish, so it was not run automatically. It may contain an infinite loop. Check your JavaScript, then press Run.");
    return;
  }

  if (trigger === RUN_TRIGGERS.EXPLICIT || options.resetScroll) {
    runPolicy.resetScroll();
  }
  const scroll = trigger === RUN_TRIGGERS.EXPLICIT ? null : runPolicy.getScroll();

  const timing = startPreviewTiming(silent ? 'live' : 'run');
  let docContent;
  try {
    markPreviewTiming(timing, 'document-build-start');
    docContent = await buildPreviewDocument({
      html,
      css,
      js: plan.effectiveJs,
      theme,
      activeRoute,
      activePath,
      restoreScroll: scroll && (scroll.x || scroll.y) ? scroll : null
    });
    markPreviewTiming(timing, 'document-build-end');
  } catch (err) {
    runPolicy.invalidateBaked();
    console.error('[Front-my-End] Preview compilation failed:', err);
    runnerServices.console?.append('error', 'Preview compilation failed. Check the console for details.');
    return;
  }

  // A newer render started while this one was building. It read the full
  // current state, so posting or queueing this older document would only
  // overwrite it.
  if (runPolicy.isStale(plan.generation)) return;

  runPolicy.recordRender(timing.id, { css, hash: workspaceHash });
  const sandboxUrl = getActiveSandboxUrl();

  if (sandboxUrl) {
    const payload = { type: 'render', content: docContent, activeRoute, timingId: timing.id };
    if (isActiveSandboxReady()) {
      postToSandbox(payload);
    } else {
      queueSandboxPayload(payload);
    }
  } else {
    console.warn(
      '[Front-my-End] No sandbox origin detected. ' +
      'Preview is running in same-origin mode (reduced isolation). ' +
      'Deploy with a preview subdomain for full security.'
    );
    showPreviewUnavailable('Preview rendering requires a cross-origin sandbox. Same-origin srcdoc execution is disabled in production.');
  }

  window.dispatchEvent(new CustomEvent('front-my-end-preview-rendered'));

  if (!silent) {
    runnerServices.console?.system('Running compilation bundle...');
    if (runnerServices.isMobile()) {
      runnerServices.setActiveMobileTab('preview');
    }
  }
}
